import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { UIMessage } from "@ai-sdk/react";
import { useAdsWorkspaceStore, type SubAgentId } from "@/features/ads";
import { useDepartmentStore } from "@/features/department";
import { ChatPanelHeader, type AvatarHeroProps } from "./ChatPanelHeader";
import { SessionDrawer } from "./SessionDrawer";
import {
  SubAgentChatPanel,
  type CompanionSubAgentPanelRenderContext,
  type SubAgentHeaderVariant,
  type SubAgentToolPartRenderer,
} from "./SubAgentChatPanel";
import { ChatMessageList } from "./agent-chat/ChatMessageList";
import { CompanionInput, type CompanionInputProps } from "./agent-chat/CompanionInput";
import { CompactToolCallCard } from "./agent-chat/CompactToolCallCard";
import {
  getToolCallFriendlyName,
  shouldSuppressToolCall,
} from "./agent-chat/toolCallFriendlyNames";
import { ADS_SUB_AGENTS } from "../lib/chatAgentRegistry";
import type { CompanionTokenUsageSnapshot } from "../lib/companionChatRuntime";
import { useSubAgentStatus } from "./hooks/useSubAgentStatus";
import { useTurnTimer, ThinkingBubble, TurnCompletedPill } from "./TurnIndicator";

// ── Sub-agent activity notifier ─────────────────────────────────────────────
// Inline chat bubble (styled like ThinkingBubble) that appears in the Marcus
// message stream while any dispatched sub-agent (Banan, Vid, Vidzz, Scout,
// Mads, GAds) is working. Clicking it jumps straight into that sub-agent's
// chat when a single agent is active (Scout → Research tab, since its UI is
// a tab not a chat); when several are working it falls back to the chat list
// so the user can pick.
function SubAgentActivityNotifier({
  scrollRef,
}: {
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  const goBackToChatList = useAdsWorkspaceStore((s) => s.goBackToChatList);
  const selectChatAgent = useAdsWorkspaceStore((s) => s.selectChatAgent);
  const setActiveTab = useAdsWorkspaceStore((s) => s.setActiveTab);
  const { isActive } = useSubAgentStatus();

  const activeAgents = ADS_SUB_AGENTS.filter((a) => isActive(a.subAgentId));
  const hasActivity = activeAgents.length > 0;
  const singleActive = activeAgents.length === 1 ? activeAgents[0] : null;
  const dataHighlightId =
    singleActive?.subAgentId === "banan" ? "marcus-chat-banan-activity-notifier" : undefined;

  useEffect(() => {
    if (hasActivity) {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [hasActivity, scrollRef]);

  const handleClick = () => {
    if (singleActive) {
      // Scout's UI is the Research tab, not a chat — mirror the click
      // behavior in RunningAgentsPanel where the avatar redirects there.
      if (singleActive.subAgentId === "scout") {
        setActiveTab("research");
      } else {
        selectChatAgent(singleActive.subAgentId);
      }
    } else {
      goBackToChatList();
    }
  };

  return (
    <AnimatePresence>
      {hasActivity && (
        <motion.button
          key="sub-agent-notifier"
          type="button"
          onClick={handleClick}
          data-highlight-id={dataHighlightId}
          aria-label={
            singleActive
              ? `Open ${singleActive.name} chat`
              : "Sub-agent activity — back to chat list"
          }
          title={singleActive ? `Open ${singleActive.name}` : "Back to chat list"}
          className="ds-caption flex items-center gap-2.5 px-4 py-3 rounded-2xl rounded-bl-sm bg-card border border-border/50 shadow-sm w-fit max-w-[85%] text-muted-foreground hover:bg-card/80 transition-colors"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 4 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          <div className="flex -space-x-1.5 shrink-0">
            {activeAgents.map((agent) => {
              const Icon = agent.icon;
              return (
                <motion.span
                  key={agent.subAgentId}
                  className="relative flex items-center justify-center w-4 h-4 rounded-full ring-1 ring-card"
                  style={{ backgroundColor: `${agent.bgHex}33` }}
                  animate={{ scale: [1, 1.15, 1] }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.2,
                    ease: "easeInOut",
                  }}
                >
                  <Icon className="w-2.5 h-2.5" style={{ color: agent.bgHex }} />
                </motion.span>
              );
            })}
          </div>
          <span className="font-medium leading-none whitespace-nowrap">
            {activeAgents.length === 1
              ? `${activeAgents[0].name.replace("Agent ", "")} working`
              : `${activeAgents.length} agents working`}
          </span>
          <motion.span
            className="w-1.5 h-1.5 rounded-full bg-primary shrink-0"
            animate={{ opacity: [1, 0.25, 1] }}
            transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
          />
        </motion.button>
      )}
    </AnimatePresence>
  );
}

// ── Tool call renderer ──────────────────────────────────────────────────────
// Marcus uses the shared CompactToolCallCard (originally lifted from Saga)
// for a denser pill with state-tinted border + click-to-expand raw output.
// `getToolCallFriendlyName` covers Marcus's ~120-tool catalog as the verb;
// we leave icon/hint resolvers unset so the card uses its Briefcase default
// (Marcus has no curated per-tool icon/hint mapping yet — Saga does).
//
// Marcus runs on the Anthropic managed-agent runtime: `marcusManagedStream`
// flags every tool chunk `dynamic: true`, so live turns arrive as
// `dynamic-tool` parts (`part.type === "dynamic-tool"`, name in `toolName`).
// Re-hydrated history instead produces
// static `tool-<name>` parts. ChatMessageList stamps `_resolvedToolName`
// onto the part for both shapes — resolve from it first, then fall back to
// the raw `dynamic-tool` / `tool-<name>` shapes so this works standalone too.
function renderCompanionToolPart(part: Record<string, unknown>) {
  const state = part.state as string | undefined;
  const output = part.output as Record<string, unknown> | undefined;
  const partType = part.type as string;
  const toolName =
    (part._resolvedToolName as string | undefined) ??
    (partType === "dynamic-tool"
      ? (part.toolName as string | undefined)
      : partType.startsWith("tool-")
        ? partType.slice(5)
        : undefined);
  const input = (part.input ?? part.args) as unknown;

  if (!toolName) return null;

  const result =
    state === "output-available"
      ? (output ?? null)
      : state === "output-error"
        ? { error: part.errorText ?? "Tool call failed" }
        : null;

  return (
    <CompactToolCallCard
      name={toolName}
      input={input}
      result={result}
      state={state ?? "input-available"}
      resolveVerb={getToolCallFriendlyName}
    />
  );
}

interface ChatPanelSubAgentConfig {
  subAgentHeaderVariant?: SubAgentHeaderVariant;
  onSubAgentBackToList?: (ctx: CompanionSubAgentPanelRenderContext) => void;
  renderSubAgentHeaderActions?: (ctx: CompanionSubAgentPanelRenderContext) => React.ReactNode;
  renderSubAgentAfterHeader?: (ctx: CompanionSubAgentPanelRenderContext) => React.ReactNode;
  renderSubAgentContent?: (ctx: CompanionSubAgentPanelRenderContext) => React.ReactNode;
  renderSubAgentToolPart?: (
    agentId: SubAgentId,
    part: Record<string, unknown>,
    index: number,
  ) => React.ReactNode;
}

interface ChatPanelProps extends ChatPanelSubAgentConfig {
  persona: { humanName: string; humanRole: string; colorHex: string };
  activeMessages: UIMessage[];
  inputProps: CompanionInputProps;
  isLoading?: boolean;
  isCompacting?: boolean;
  tokenUsage?: CompanionTokenUsageSnapshot | null;
  isListening: boolean;
  isSecureMicContext: boolean;
  avatarMuted: boolean;
  avatarSpeaking: boolean;
  voiceVolume?: number;
  setVoiceVolume?: (v: number) => void;
  avatarHero?: AvatarHeroProps;
  toggleListen: () => void;
  setAvatarMuted: (m: boolean) => void;
  voiceTranscriptRef?: React.RefObject<string>;
  /** Spoken-audio timeline ref for the voice transcript feed. */
  timelineRef?: React.RefObject<{
    segments: unknown[];
    totalDurationMs: number;
    speakStartedAt: number;
  }>;
}

export function ChatPanel({ ...props }: ChatPanelProps) {
  const selectedChatAgent = useAdsWorkspaceStore((s) => s.selectedChatAgent);

  if (selectedChatAgent) {
    const renderToolPart: SubAgentToolPartRenderer | undefined = props.renderSubAgentToolPart
      ? (part, index) => props.renderSubAgentToolPart?.(selectedChatAgent, part, index)
      : undefined;
    const ctx: CompanionSubAgentPanelRenderContext = {
      agentId: selectedChatAgent,
      renderToolPart,
    };
    const content = props.renderSubAgentContent?.(ctx);

    return (
      <SubAgentChatPanel
        agentId={selectedChatAgent}
        headerVariant={props.subAgentHeaderVariant}
        onBackToList={
          props.onSubAgentBackToList ? () => props.onSubAgentBackToList?.(ctx) : undefined
        }
        headerActions={props.renderSubAgentHeaderActions?.(ctx)}
        afterHeader={props.renderSubAgentAfterHeader?.(ctx)}
        renderToolPart={renderToolPart}
      >
        {content}
      </SubAgentChatPanel>
    );
  }

  return <MarcusChatPanel {...props} />;
}

function MarcusChatPanel({
  persona,
  activeMessages,
  inputProps,
  isLoading,
  isCompacting,
  tokenUsage,
  isListening,
  isSecureMicContext,
  avatarMuted,
  avatarSpeaking,
  voiceVolume = 1,
  setVoiceVolume,
  avatarHero,
  toggleListen,
  setAvatarMuted,
  voiceTranscriptRef: _voiceTranscriptRef,
  timelineRef: _timelineRef,
}: ChatPanelProps) {
  const companionSessionControls = useAdsWorkspaceStore((s) => s.companionSessionControls);
  const { companionMode, setCompanionMode } = useDepartmentStore();

  const [sessionDrawerOpen, setSessionDrawerOpen] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const isChatMode = companionMode === "chat";

  const activeSession = companionSessionControls?.sessions.find(
    (s) => s.id === companionSessionControls.activeSessionId,
  );
  const hasMarcusSessionControls = companionSessionControls != null;
  const sessionControlsBusy =
    !!companionSessionControls?.isResolving || !!companionSessionControls?.isCreating;

  // Turn timer: ticks while the agent is working on the user's last message,
  // and stashes the duration of the last-completed turn so we can render a
  // "Took Xs" pill after the response lands.
  const { elapsedMs: turnElapsedMs, lastDurationMs: lastTurnDurationMs } = useTurnTimer(isLoading);

  // The current bubble gate (`isLoading && last.role !== "assistant"`) hides
  // the indicator the moment the agent emits ANY assistant part, even if the
  // turn is still mid-stream planning another tool call. Instead, show the
  // bubble persistently while `isLoading` is true — except when the tail part
  // is a tool currently executing, because the ToolCallCard itself already
  // signals progress and we'd be double-signaling.
  const lastMessage = activeMessages[activeMessages.length - 1];
  // Walk backwards past suppressed tool parts (e.g. bash JWT regen) — those
  // don't render anything, so they shouldn't count as "a tool is visibly
  // executing" for the purpose of hiding the thinking indicator.
  let lastPart: { type?: string; state?: string; toolName?: string; input?: unknown } | undefined;
  if (lastMessage?.parts?.length) {
    for (let i = lastMessage.parts.length - 1; i >= 0; i -= 1) {
      const p = lastMessage.parts[i] as {
        type?: string;
        state?: string;
        toolName?: string;
        input?: unknown;
      };
      const isToolLike =
        typeof p.type === "string" && (p.type === "dynamic-tool" || p.type.startsWith("tool-"));
      if (isToolLike) {
        const name = p.type === "dynamic-tool" ? p.toolName : p.type!.slice(5);
        if (name && shouldSuppressToolCall(name, p.input)) continue;
      }
      lastPart = p;
      break;
    }
  }
  const isLastPartToolRunning =
    !!lastPart &&
    typeof lastPart.type === "string" &&
    (lastPart.type === "dynamic-tool" || lastPart.type.startsWith("tool-")) &&
    (lastPart.state === "input-streaming" || lastPart.state === "input-available");
  const showThinkingBubble = isCompacting || (!!isLoading && !isLastPartToolRunning);

  // Auto-scroll chat
  useEffect(() => {
    if (isChatMode) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeMessages, isChatMode]);

  // Marcus chat view
  return (
    <div className="flex flex-col h-full relative">
      <ChatPanelHeader
        sessionName={activeSession?.name}
        sessionCreatedAt={activeSession?.createdAt}
        isStreaming={isLoading}
        isCompacting={isCompacting}
        turnElapsedMs={turnElapsedMs}
        tokenUsage={tokenUsage}
        avatarSpeaking={avatarSpeaking}
        avatarHero={avatarHero}
        onOpenSessions={hasMarcusSessionControls ? () => setSessionDrawerOpen(true) : undefined}
        onCreateSession={companionSessionControls?.onCreateSession}
        sessionControlsDisabled={sessionControlsBusy}
        voiceControls={{
          isChatMode,
          isListening,
          isSecureMicContext,
          avatarMuted,
          voiceVolume,
          toggleListen,
          setAvatarMuted,
          setVoiceVolume: setVoiceVolume ?? (() => {}),
          setCompanionMode,
        }}
      />

      {/* Chat content */}
      <div className="flex-1 flex flex-col min-h-0 bg-background/30">
        {isChatMode ? (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-2 ds-scrollbar">
              <div className="flex flex-col gap-2 pt-2">
                <ChatMessageList
                  messages={activeMessages}
                  compact
                  renderToolPart={renderCompanionToolPart}
                />
                {showThinkingBubble ? (
                  <ThinkingBubble
                    name={persona.humanName}
                    isCompacting={isCompacting}
                    elapsedMs={turnElapsedMs}
                  />
                ) : lastTurnDurationMs != null && activeMessages.length > 0 ? (
                  <TurnCompletedPill ms={lastTurnDurationMs} />
                ) : null}
                <SubAgentActivityNotifier scrollRef={chatEndRef} />
                <div ref={chatEndRef} />
              </div>
            </div>
            <div className="shrink-0 bg-card/30">
              <CompanionInput {...inputProps} compact />
            </div>
          </>
        ) : (
          /* Voice mode placeholder. */
          <div className="ds-caption flex-1 flex items-center justify-center text-muted-foreground/40">
            Voice mode active
          </div>
        )}
      </div>

      {/* Session drawer overlay */}
      <SessionDrawer open={sessionDrawerOpen} onClose={() => setSessionDrawerOpen(false)} />
    </div>
  );
}
